import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import profilePic from '../images/profile_pic_zoom.jpg';
import hubbleImg from '../images/hubble_birthday.jpg';

const NAV_CARDS = [
  {
    num: '02',
    title: 'The person',
    desc: 'Uno, Nessuno e Centomila',
    href: '/the-person',
    accent: '#7cb8a0',
  },
  {
    num: '03',
    title: 'Projects',
    desc: 'Building & Creating',
    href: '/projects',
    accent: '#8a9fd4',
  },
  {
    num: '04',
    title: 'Travel Stories',
    desc: 'Exploring the World',
    href: '/travel',
    accent: '#c4a24a',
  },
  {
    num: '05',
    title: 'Career & Ed',
    desc: 'Automation and AI',
    href: '/career',
    accent: '#a07bc4',
  },
  {
    num: '06',
    title: 'Blog',
    desc: "What's in my mind",
    href: '/blog',
    accent: '#c47b8a',
  },
  {
    num: '07',
    title: 'Connect',
    desc: "Let's Talk",
    href: '/connect',
    accent: '#7bc4b0',
  },
];

export default function Home() {
  const [hubbleOpen, setHubbleOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setHubbleOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div style={{ minHeight: '100vh', padding: '48px', position: 'relative', overflow: 'hidden' }}>

      {/* Hubble birthday background — full page, behind everything */}
      <div aria-hidden="true" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}>
        <img
          src={hubbleImg}
          alt=""
          className="hubble-bg"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', filter: 'blur(3px)', transform: 'scale(1.02)' }}
        />
      </div>

      {/* Ghost background text */}
      <div aria-hidden="true" style={{
        position: 'absolute',
        top: '-20px',
        right: '16px',
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontSize: 'clamp(120px, 20vw, 220px)',
        fontWeight: 700,
        letterSpacing: '-0.04em',
        color: 'var(--surface-2)',
        lineHeight: 1,
        userSelect: 'none',
        pointerEvents: 'none',
        zIndex: 1,
      }}>
        Terzi
      </div>

      {/* Easter egg crosshair */}
      <div
        className="hubble-crosshair"
        onClick={() => setHubbleOpen(true)}
        title="What did the universe look like when I was born?"
        style={{
          position: 'absolute',
          top: '42%',
          right: '18%',
          zIndex: 2,
          cursor: 'crosshair',
          userSelect: 'none',
          fontSize: '1.3rem',
          lineHeight: 1,
        }}
      >
        ✛
      </div>

      {/* Hubble full-screen overlay — rendered via portal to escape containing-block transforms */}
      {ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 300,
            background: '#000000',
            opacity: hubbleOpen ? 1 : 0,
            pointerEvents: hubbleOpen ? 'all' : 'none',
            transition: 'opacity 0.5s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 24px',
            cursor: 'pointer',
          }}
          onClick={() => setHubbleOpen(false)}
        >
          <img
            src={hubbleImg}
            alt="Hubble Space Telescope image — August 23rd 1997"
            style={{
              maxHeight: '72vh',
              maxWidth: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: '1.05rem',
            color: 'rgba(255,255,255,0.85)',
            textAlign: 'center',
            marginTop: '24px',
            marginBottom: 0,
            maxWidth: '540px',
            lineHeight: 1.65,
          }}>
            This is what the Hubble Space Telescope saw on August 23rd, 1997 — the day I was born.
          </p>
          <button
            onClick={() => setHubbleOpen(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '24px',
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.3)',
              fontSize: '1.3rem',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '8px',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
          >
            ✕
          </button>
        </div>,
        document.body
      )}

      {/* Hero section */}
      <div style={{ position: 'relative', zIndex: 1, marginBottom: '56px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '32px', flexWrap: 'wrap' }}>

          {/* Profile image */}
          <div style={{
            width: '96px',
            height: '96px',
            borderRadius: '6px',
            overflow: 'hidden',
            flexShrink: 0,
            border: '1px solid var(--border)',
          }}>
            <img
              src={profilePic}
              alt="Massimo Terzi"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>

          {/* Name + tagline */}
          <div>
            <div className="eyebrow" style={{ marginBottom: '8px' }}>01 — Home</div>
            <h1 style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: 'clamp(2.4rem, 5vw, 4rem)',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              lineHeight: 0.95,
              color: 'var(--text)',
              margin: 0,
            }}>
              Massimo Terzi
            </h1>
            <p style={{
              fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              fontSize: '0.95rem',
              color: 'var(--text-3)',
              marginTop: '10px',
              marginBottom: 0,
              lineHeight: 1.5,
            }}>
              Human Being · Citizen of the world · Engineer
            </p>
          </div>
        </div>

        {/* Personal motto — below tagline, above quote */}
        <p style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontStyle: 'italic',
          fontSize: '1.15rem',
          fontWeight: 500,
          color: 'var(--text-2)',
          marginTop: '32px',
          marginBottom: 0,
          maxWidth: '520px',
          lineHeight: 1.5,
        }}>
          Search the beauty in the balance of numbers, in the sound of words, in the harmony of colors.
        </p>

        {/* Dante quote */}
        <div className="quote-block dante-quote" style={{ marginTop: '28px', maxWidth: '520px' }}>
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: 'clamp(1rem, 3.8vw, 1.4rem)',
            fontWeight: 500,
            color: 'var(--text)',
            lineHeight: 1.4,
            margin: 0,
          }}>
            "Fatti non foste a viver come bruti,<br />
            ma per seguir virtute e canoscenza"
          </p>
          <p style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.08em',
            color: 'var(--text-3)',
            marginTop: '10px',
            marginBottom: '6px',
          }}>
            — DANTE ALIGHIERI, INFERNO CANTO XXVI
          </p>
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: 'clamp(0.85rem, 2.8vw, 1.05rem)',
            fontWeight: 400,
            color: 'var(--text-2)',
            margin: 0,
            lineHeight: 1.5,
          }}>
            "You were not made to live like brutes,<br />
            but to pursue virtue and knowledge"
          </p>
        </div>
      </div>

      {/* Navigation cards grid */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '12px',
      }}>
        {NAV_CARDS.map((card) => (
          <Link
            key={card.href}
            to={card.href}
            style={{ textDecoration: 'none' }}
          >
            <div
              className="card-hover"
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '20px',
                cursor: 'pointer',
                borderTop: `2px solid ${card.accent}`,
              }}
            >
              <div style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.6rem',
                color: 'var(--text-3)',
                letterSpacing: '0.08em',
                marginBottom: '10px',
              }}>
                {card.num}
              </div>
              <h2 style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontSize: '1.35rem',
                fontWeight: 600,
                color: 'var(--text)',
                margin: '0 0 4px',
                letterSpacing: '-0.01em',
              }}>
                {card.title}
              </h2>
              <p style={{
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                fontSize: '0.75rem',
                color: 'var(--text-3)',
                margin: 0,
              }}>
                {card.desc}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
